import { Select, type SelectProps } from 'antd';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { TColumns } from '@client/types/antd.type';
import type { ISelectItemProps } from '@client/utils/table/column.util';

type TProps = {
  columnsList: ISelectItemProps[];
  selectedColumns: TColumns['key'][];
  onChange: (value: string[]) => void;
};

/**
 * HideColumns is a component that allows users to select which columns they want to hide
 * @param {TProps} props
 * @returns {React.JSX.Element}
 * @example
 * <HideColumns
 *   columns={['name', 'age', 'address']}
 *   selected={['name', 'age']}
 *   onChange={(value: string[]) => { console.log(value); }}
 * />
 */
export const HideColumns = (props: TProps): React.JSX.Element => {
  const { t } = useTranslation();

  const { selectedColumns = [], columnsList = [], onChange } = props;

  if (!columnsList?.length) {
    return <></>; // Return an empty fragment if no columns are provided
  }

  const placeholder = t('form.inputPlaceholder', {
    field: t('form.toShow'),
    type: t('actions.select'),
  });

  const sharedProps: SelectProps = {
    mode: 'multiple',
    style: { minWidth: 250 },
    options: [
      {
        label: <span>{placeholder}</span>,
        title: placeholder as string,
        options: columnsList,
      },
    ],
    placeholder,
    maxTagCount: 'responsive',
  };

  /**
   * Handles the change event for the Select component.
   * It updates the selected columns if the number of selected items is greater than zero.
   * If no items are selected, it prevents the update.
   * @param {string[]} values - An array of selected column keys.
   */
  const handleChange = (values: string[]): void => {
    // Prevent update if too few items are unselected
    const shouldDisable = values.length < 1;

    if (shouldDisable) {
      return;
    }

    onChange?.(values);
  };

  const selectProps: SelectProps = {
    value: selectedColumns,
    onChange: handleChange,
  };

  return <Select {...sharedProps} {...selectProps} />;
};
